'use client';

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useUser } from '@/lib/context/user-context';
import { usePathname } from 'next/navigation';

interface SavedWordsContextType {
  savedWords: Record<string, boolean>;
  savedWordsLoaded: boolean;
  isCardSaved: (word: string) => boolean;
  markWordAsSaved: (word: string) => void;
}

const SavedWordsContext = createContext<SavedWordsContextType | undefined>(undefined);

export function SavedWordsProvider({ children }: { children: React.ReactNode }) {
  const { user } = useUser();
  const pathname = usePathname();
  const [savedWords, setSavedWords] = useState<Record<string, boolean>>({});
  const [savedWordsLoaded, setSavedWordsLoaded] = useState(false);
  const inFlightRef = React.useRef<Promise<void> | null>(null);

  const shouldLoadSavedWords = React.useMemo(() => {
    if (!pathname) return true;
    if (pathname === '/episodes') {
      return false;
    }
    return true;
  }, [pathname]);

  // Load saved words when user is available
  useEffect(() => {
    if (user?.id && !savedWordsLoaded && shouldLoadSavedWords) {
      if (inFlightRef.current) return;
      const loadSavedWords = async () => {
        try {
          const response = await fetch(`/api/user/saved-words?userId=${user.id}`);
          if (response.ok) {
            const data = await response.json();
            if (data.savedWords) {
              const savedWordsMap: Record<string, boolean> = {};
              data.savedWords.forEach((word: any) => {
                savedWordsMap[word.word.toLowerCase()] = true;
              });
              setSavedWords(savedWordsMap);
              setSavedWordsLoaded(true);
            } else {
              setSavedWords({});
              setSavedWordsLoaded(true);
            }
          }
        } catch (error) {
          console.error('Failed to load saved words:', error);
          setSavedWords({});
          setSavedWordsLoaded(true);
        } finally {
          inFlightRef.current = null;
        }
      };
      inFlightRef.current = loadSavedWords();
    }
  }, [user?.id, savedWordsLoaded, shouldLoadSavedWords]);

  // Reset when user changes
  useEffect(() => {
    if (!user?.id) {
      setSavedWords({});
      setSavedWordsLoaded(false);
    }
  }, [user?.id]);

  const isCardSaved = useCallback((word: string) => {
    const wordKey = word.toLowerCase();
    if (savedWordsLoaded) {
      return savedWords[wordKey] === true;
    }
    return false;
  }, [savedWords, savedWordsLoaded]);

  const markWordAsSaved = useCallback((word: string) => {
    setSavedWords(prev => ({ ...prev, [word.toLowerCase()]: true }));
  }, []);

  return (
    <SavedWordsContext.Provider
      value={{
        savedWords,
        savedWordsLoaded,
        isCardSaved,
        markWordAsSaved,
      }}
    >
      {children}
    </SavedWordsContext.Provider>
  );
}

export function useSavedWordsContext() {
  const context = useContext(SavedWordsContext);
  if (context === undefined) {
    throw new Error('useSavedWordsContext must be used within a SavedWordsProvider');
  }
  return context;
}
